/**
 * @file Boite de confirmation generique (texte i18n, Confirmer / Annuler) renvoyant une Promise<boolean>.
 */
import type { TranslationKey } from '@i18n/index';
import { Component } from '../core/Component';
import { h } from '../core/dom';
import type { UiContext } from '../UiContext';

export class ConfirmDialog extends Component {
  private resolveFn: ((v: boolean) => void) | null = null;

  constructor(
    ctx: UiContext,
    private readonly textKey: TranslationKey,
  ) {
    super(ctx, 'div', 'modal confirm');
  }

  static ask(ctx: UiContext, parent: HTMLElement, textKey: TranslationKey): Promise<boolean> {
    const d = new ConfirmDialog(ctx, textKey);
    return new Promise<boolean>((resolve) => {
      d.resolveFn = resolve;
      d.mount(parent);
    });
  }

  protected build(): void {
    const t = this.ctx.i18n;
    this.el.setAttribute('data-ui-block', '');
    this.el.setAttribute('role', 'alertdialog');
    this.el.setAttribute('aria-modal', 'true');
    const ok = h('button', { class: 'btn primary', type: 'button', onclick: () => this.settle(true) }, t.t('common.confirm'));
    const cancel = h('button', { class: 'btn', type: 'button', onclick: () => this.settle(false) }, t.t('common.cancel'));
    this.el.append(h('div', { class: 'dialog panel' }, h('p', { class: 'dialog-text' }, t.t(this.textKey)), h('div', { class: 'dialog-actions' }, cancel, ok)));

    // Clic sur le fond (hors carte) ou Echap = annuler.
    this.on(this.el, 'pointerdown', (e) => {
      if (e.target === this.el) this.settle(false);
    });
    this.on(document, 'keydown', (e) => {
      if (e.key === 'Escape') this.settle(false);
    });
  }

  private settle(value: boolean): void {
    const r = this.resolveFn;
    this.resolveFn = null;
    r?.(value);
    this.destroy();
  }

  override destroy(): void {
    // Detruit de l'exterieur (changement de langue...) : la promesse se resout sur "annuler".
    const r = this.resolveFn;
    this.resolveFn = null;
    super.destroy();
    r?.(false);
  }
}
